import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationRuleAlertListQuery } from "../../../api/network/adminApiServices";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { FilterType } from "../../../api/types/Admin";
import { TLegacyRule } from "../../../api/types/Rule";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useListingFilters } from "../../../hooks/listingFilter";
import { routeUrls } from "../../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { SYSTEM_CRON_RULE_OPTIONS } from "../../admin-details/manage-rule-alert/constant";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import AddNewRule from "./createNewRule";

const all_columns = [
  // "Sr. No",
  "Rule ID",
  "Description",
  "Selector",
  "Rule Frequency",
  "Active",
];

const columnOrderingFields: Record<string, string> = {
  "Rule ID": "ruleid",
  Description: "description",
  Selector: "selector",
  "Rule Frequency": "ruletag",
  Active: "isactive",
};

const ScreenManageRuleAlert = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", { keyPrefix: "manageRuleAlert" });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { rules: orgRulesQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  const { getUserACLAccessLevel } = useACL();

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.Rules);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        rules: {
          ...orgRulesQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        rules: {
          ...orgRulesQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // filter mechainism
  const { filters } = useListingFilters();
  const [activeFilterSlug, setActiveFilterSlug] = React.useState<FilterType>(
    filters?.[0]?.slug
  );

  // updating query params on filter change
  useEffect(() => {
    const newParams = {
      ...listingQueryParams,
      rules: {
        ...orgRulesQueryParams,
        isactive: undefined,
        page: 1,
        ...(activeFilterSlug === "both"
          ? {}
          : {
              isactive: activeFilterSlug === "active" ? 1 : 0,
            }),
      },
    };
    dispatch(setListingQueryParams(newParams));
  }, [activeFilterSlug]);

  // fetching rules data
  const {
    data: dataOrgRules,
    isFetching: isFetchingOrgRules,
    error,
  } = useOrganizationRuleAlertListQuery(orgRulesQueryParams);
  const { count, next, previous, results } = dataOrgRules || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgRules]);

  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TLegacyRule[])).map(
        (item: TLegacyRule, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.adminChildren.manage_rule_alert}/${item.ruleid}`,
          cellData: [
            // (listingQueryParams.users.page_size * (listingQueryParams.users.page - 1)) + index + 1, // sr_no by page
            item?.ruleid, // id:
            item?.description || "-", // description:
            item?.selector || "-", // selector:
            SYSTEM_CRON_RULE_OPTIONS.find(
              (rule) => rule.value === item?.ruletag
            )?.label || "-", // cron_rule:
            item?.isactive === 1 ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : item?.isactive === 0 ? (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ) : (
              "-"
            ), // active:
            //   !!item?.lastlogintime ? formattedDateTime(item?.lastlogintime * 1000) : '-' // last_login:
          ].filter((_, index) => columns[index].show),
        })
      )
    : [];

  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_RULE) >=
            ROLE_ACCESS_LEVELS.ALL && {
            buttonText: t("add_new"),
            buttonCallback: () => {
              dispatch(setModalsData({ ...modalsState, showCreateRule: true }));
            },
          })}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgRulesQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgRulesQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgRulesQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
          filters={filters}
          activeFilterSlug={activeFilterSlug}
          handleFilterChange={(slug) => setActiveFilterSlug(slug)}
        />
        ̦
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Rules}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgRules} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgRules}
            listingQueryParams={orgRulesQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgRulesQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgRules && (
            <Pagination
              pageSize={orgRulesQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    rules: {
                      ...orgRulesQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count ? Math.ceil(count / orgRulesQueryParams.page_size) : 1
              }
              forcePage={orgRulesQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    rules: { ...orgRulesQueryParams, page: data?.selected + 1 },
                  })
                );
              }}
              paginCurrentPage={orgRulesQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <AddNewRule />
    </>
  );
};

export default ScreenManageRuleAlert;
